import { trapFocus } from './global.js';

/**
 * <search-overlay>
 * Recherche predictive en superposition. A chaque frappe le client
 * demande la section `predictive-search` au serveur et remplace le
 * fragment : aucun rendu de resultat n'est ecrit en JavaScript.
 *
 * La page /search reste la source de verite ; l'overlay evite seulement
 * de faire quitter sa page au client pour une recherche.
 */

const DEBOUNCE = 220;
const MIN_LENGTH = 2;

class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.releaseFocus = null;
    this.timer = null;
    this.controller = null;

    document.querySelectorAll('[data-search-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
    });

    this.querySelectorAll('[data-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.addEventListener('rc:dismiss', () => this.close());

    // Echap, meme si le focus a quitte le piege.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    // Clic hors du panneau : le geste attendu pour fermer une superposition.
    document.addEventListener('click', (event) => {
      if (this.hidden) return;
      if (event.target.closest('[data-search-open]')) return;
      if (event.target.closest('.rc-search__panel')) return;
      this.close();
    });

    if (this.input) {
      this.input.addEventListener('input', () => this.schedule());
    }
  }

  open(trigger) {
    this.hidden = false;
    this.trigger = trigger;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this.releaseFocus = trapFocus(this, this.input);
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    if (this.releaseFocus) {
      this.releaseFocus();
      this.releaseFocus = null;
    }
  }

  schedule() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.search(), DEBOUNCE);
  }

  async search() {
    const terms = this.input.value.trim();

    if (terms.length < MIN_LENGTH) {
      this.results.innerHTML = '';
      return;
    }

    // Une frappe annule la requete precedente : sinon une reponse lente
    // ecrase une reponse plus recente.
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const url = new URL(window.rcRoutes?.predictiveSearch || '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '4');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fragment = parsed.querySelector('[data-predictive-results]');
      if (fragment) this.results.replaceChildren(...fragment.childNodes);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[search-overlay]', error);
    }
  }
}

if (!customElements.get('search-overlay')) {
  customElements.define('search-overlay', SearchOverlay);
}
